import React, { useEffect, useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
} from '@mui/material';
import DatePicker, { registerLocale } from 'react-datepicker';
import pt from 'date-fns/locale/pt';
import de from 'date-fns/locale/de';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parseISO } from 'date-fns';
import { useService } from 'contexts/ServiceContextProvider';
import { useSidebar } from 'contexts/SidebarContextProvider';
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import "../../css/components/Calendar/VacationRequestModal.css";
import "../../css/components/Calendar/RefuteRequestModal.css";
import "../../css/main.css";

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';


import InfoIcon from '@mui/icons-material/Info';
import { formattedHistory } from "./formatVacationHistory"
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip';

const RefuteRequestModal = ({ open, handleClose, refutedVacations, handleDelete }) => {
    const { t } = useTranslation();
    registerLocale('pt', pt);
    registerLocale('de', de);

    const { DeleteVacationRequest, UpdatePendingVacationRequest, getMyRemainingWorkspaceVacationDays } = useService();
    const { isSidebarOpen } = useSidebar();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [error, setError] = useState('');
    const [totalDays, setTotalDays] = useState(0);
    const [totalWeekdays, setTotalWeekdays] = useState(0);

    const { workspaceId } = useParams();

    const currentVacation = refutedVacations?.[currentIndex];

    const [vacationDays, setVacationDays] = useState({});

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        async function fetchVacationDays() {
            if (workspaceId && currentVacation) {
                try {
                    const response = await getMyRemainingWorkspaceVacationDays(workspaceId);
                    setVacationDays(response.data.vacation_balance);
                } catch (error) {
                    console.error('Failed to fetch vacation days:', error);
                }
            }
        }

        fetchVacationDays();
    }, [workspaceId, currentVacation]);

    // State for vacation data
    const [vacationData, setVacationData] = useState({
        title: '',
        description: '',
        type: 'RV',
        date_start: new Date(),
        date_end: new Date(),
        visibility: false,
        workspaceId: workspaceId || '',
        do_not_deduct: false,
    });

    useEffect(() => {
        if (currentVacation) {
            setVacationData({
                title: currentVacation.title || '',
                description: currentVacation.description || '',
                type: 'RV',
                date_start: currentVacation.date_start ? parseISO(currentVacation.date_start) : new Date(),
                date_end: currentVacation.date_end ? parseISO(currentVacation.date_end) : new Date(),
                visibility: currentVacation.visibility || false,
                workspaceId: workspaceId || '',
                do_not_deduct: currentVacation.do_not_deduct || false,
            });
        }
    }, [currentVacation, workspaceId]);

    // Calculate total days and weekdays
    useEffect(() => {
        const start = vacationData.date_start instanceof Date && !isNaN(vacationData.date_start) ? vacationData.date_start : new Date();
        const end = vacationData.date_end instanceof Date && !isNaN(vacationData.date_end) ? vacationData.date_end : new Date();
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setTotalDays(diffDays);

        const weekdaysCount = getWeekdaysCount(start, end);
        setTotalWeekdays(weekdaysCount);
    }, [vacationData.date_start, vacationData.date_end]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setVacationData({
            ...vacationData,
            [name]: value,
        });
    };

    const handleDateChange = (name, date) => {
        let newVacationData = { ...vacationData, [name]: date };

        if (name === 'date_start' && date > vacationData.date_end) {
            setError(t("calendar.vacation_err1"));
        } else if (name === 'date_end' && date < vacationData.date_start) {
            setError(t("calendar.vacation_err2"));
        } else {
            setError('');

            const start = new Date(newVacationData.date_start);
            const end = new Date(newVacationData.date_end);
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            setTotalDays(diffDays);

            const weekdaysCount = getWeekdaysCount(start, end);
            setTotalWeekdays(weekdaysCount);
        }

        setVacationData(newVacationData);
    };

    const getWeekdaysCount = (start, end) => {
        let count = 0;
        const current = new Date(start);
        while (current <= end) {
            const day = current.getDay();
            if (day !== 0 && day !== 6) count++;
            current.setDate(current.getDate() + 1);
        }
        return count;
    };

    const handleVisibilityChange = (event) => {
        setVacationData({
            ...vacationData,
            visibility: event.target.checked,
        });
    };

    const handleDeductabilityChange = (event) => {
        setVacationData({
            ...vacationData,
            do_not_deduct: event.target.checked,
        });
    };

    const handleSubmit = async () => {
        if (vacationData.date_start > vacationData.date_end) {
            setError(t("calendar.vacation_err1"));
            return;
        }

        const formattedData = {
            ...vacationData,
            date_start: format(vacationData.date_start, 'yyyy-MM-dd'),
            date_end: format(vacationData.date_end, 'yyyy-MM-dd'),
        };

        try {
            await UpdatePendingVacationRequest(workspaceId, [currentVacation.id], 'resubmit', '', formattedData);
            window.dispatchEvent(new CustomEvent('vacationSubmitted'));

            if (currentIndex < refutedVacations.length - 1) {
                setCurrentIndex(currentIndex + 1);
            } else {
                handleClose();
            }
        } catch (error) {
            console.error('Error resubmitting vacation:', error);
        }
    };

    const handleDeleteVacation = async () => {
        try {
            await DeleteVacationRequest(workspaceId, [currentVacation.id]);

            // Move to the next refuted vacation request, or close if all are resolved
            if (currentIndex < refutedVacations.length - 1) {
                setCurrentIndex(currentIndex + 1);
            } else {
                handleClose(); // All refuted requests are resolved
            }
        } catch (error) {
            console.error('Error deleting vacation:', error);
        }
    };

    return (
        <Modal
            open={open}
            onClose={() => { }}
            aria-labelledby="refute-request-modal-title"
            aria-describedby="refute-request-modal-description"
            className={`request-vacation-modal ${isSidebarOpen() ? 'sidebar-open' : ''}`}
        >
            <Box className="request-vacation-main-style">
                <Typography id="refute-request-modal-title" variant="h6" component="h2" className="white-txt-color">
                    {t("calendar.vacation_resubmit_count", {count1: currentIndex + 1, count2: refutedVacations?.length})}
                </Typography>

                {/* Display the admin's refutal message */}
                {currentVacation?.refute_description && (
                    <Box className="refute-message-box">
                        <Typography variant="body2" className="refute-message-title">
                            <strong>{t("calendar.vacation_feedback")}</strong> {currentVacation.refute_description}
                        </Typography>
                    </Box>
                )}

                <TextField
                    label={t("calendar.event_description")}
                    name="description"
                    value={vacationData.description}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={3}
                    InputLabelProps={{ className: "request-vacation-type-title" }}
                    InputProps={{
                        className: "request-vacation-type-select"
                    }}
                    onChange={handleChange}
                />
                <FormControl margin="normal" className="request-vacation-form">
                    <InputLabel className="request-vacation-type-title">{t("calendar.vacation_type")}</InputLabel>
                    <Select
                        label={t("calendar.vacation_type")}
                        name="type"
                        value={vacationData.type}
                        onChange={handleChange}
                        variant="outlined"
                        className="request-vacation-type-select"
                        MenuProps={{
                            PaperProps: {
                                className: "request-vacation-type-select",
                            },
                        }}
                    >
                        <MenuItem value="RV">{t("calendar.vacation_type1")}</MenuItem>
                        <MenuItem value="SL">{t("calendar.vacation_type2")}</MenuItem>
                        <MenuItem value="PL">{t("calendar.vacation_type3")}</MenuItem>
                        <MenuItem value="MPL">{t("calendar.vacation_type4")}</MenuItem>
                    </Select>
                </FormControl>
                <div className="request-vacation-date-info">
                    <Box className="request-vacation-date-picker">
                        <DatePicker
                            selected={vacationData.date_start}
                            onChange={(date) => handleDateChange('date_start', date)}
                            dateFormat="yyyy-MM-dd"
                            selectsStart
                            startDate={vacationData.date_start}
                            endDate={vacationData.date_end}
                            customInput={
                                <TextField
                                    label={t("global.start_date")}
                                    margin="normal"
                                    variant="outlined"
                                    InputLabelProps={{ className: "request-vacation-type-title" }}
                                    InputProps={{
                                        className: "request-vacation-type-select",
                                    }}
                                />
                            }
                            locale={t("global.short_lng")}
                        />
                        <DatePicker
                            selected={vacationData.date_end}
                            onChange={(date) => handleDateChange('date_end', date)}
                            dateFormat="yyyy-MM-dd"
                            selectsEnd
                            startDate={vacationData.date_start}
                            endDate={vacationData.date_end}
                            minDate={vacationData.date_start}
                            customInput={
                                <TextField
                                    label={t("calendar.event_end_date")}
                                    margin="normal"
                                    variant="outlined"
                                    InputLabelProps={{ className: "request-vacation-type-title" }}
                                    InputProps={{
                                        className: "request-vacation-type-select",
                                    }}
                                />
                            }
                            locale={t("global.short_lng")}
                        />
                    </Box>
                    <Box className="request-vacation-info-box">
                        <Typography variant="body1" className="request-vacation-info-title">
                            <strong>{t("calendar.vacation_info")}</strong>
                        </Typography>
                        <Typography variant="body2" className="request-vacation-type-title">
                            {t("calendar.vacation_per_year", { count: vacationDays.remaining_vacation_days })}
                        </Typography>
                        <Typography variant="body2" className="request-vacation-type-title">
                            {t("calendar.vacation_rollovers", { count: vacationDays.rollover_days })}
                            {vacationDays.rollover_days > 0 && (
                                <ScrollAwareTooltip
                                    title={<span dangerouslySetInnerHTML={{ __html: formattedHistory(vacationDays.rollover_days_history, t) }} />}
                                >
                                    <InfoIcon className="white-txt-color" />
                                </ScrollAwareTooltip>
                            )}
                        </Typography>
                        <Typography variant="body2" className="request-vacation-type-title">
                            {t("calendar.vacation_used_days", { count: vacationDays.used_vacation_days })}
                        </Typography>
                        <Typography variant="body2" className="request-vacation-type-title">
                            {t("calendar.vacation_total_days", { count: vacationDays.total_available_days })}
                        </Typography>
                        {(vacationDays.pending_vacation_days || 0) > 0 && (
                            <Typography variant="body2" className="request-vacation-type-title" style={{ color: '#f0a500' }}>
                                {t("calendar.vacation_pending_days", { count: vacationDays.pending_vacation_days })}
                            </Typography>
                        )}
                    </Box>
                </div>

                <div className="request-vacation-total-days">
                    <Typography variant="subtitle1" className="request-vacation-total-count">
                        {t("calendar.vacation_total_count", { count1: totalDays })}
                    </Typography>
                    <Typography variant="subtitle1">
                        {t("calendar.vacation_total_count_weekdays", { count1: totalWeekdays })}
                    </Typography>
                </div>

                {error && (
                    <Typography variant="body2" className="request-vacation-error">
                        {error}
                    </Typography>
                )}

                <Box className="request-vacation-flex">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={vacationData.visibility}
                                onChange={handleVisibilityChange}
                                name="visibility"
                                color="primary"
                            />
                        }
                        label={t("calendar.event_public")}
                        className="request-vacation-total-count"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={vacationData.do_not_deduct}
                                onChange={handleDeductabilityChange}
                                name="do_not_deduct"
                                color="primary"
                            />
                        }
                        label={t("calendar.vacation_no_deduct")}
                        className="request-vacation-total-count"
                    />
                </Box>
                <Box className="buttons-flex request-vacation-buttons-box">
                    <Button
                        variant="outlined"
                        className="decline-button request-vacation-cancel-button"
                        onClick={handleDeleteVacation}
                    >
                        <CancelIcon style={{ marginRight: 4 }} />
                        {t("modal.delete")}
                    </Button>
                    <Button
                        variant="contained"
                        className="accept-button"
                        onClick={handleSubmit}
                        disabled={!!error}
                    >
                        <CheckCircleIcon style={{ marginRight: 4 }} />
                        {t("calendar.vacation_resubmit")}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default RefuteRequestModal;